/**
 * Advanced Analysis API Service
 * Handles communication with the Advanced Analysis Lambda endpoint
 */

import {get, head} from "aws-amplify/api";
import {AnalysisResult, AnalysisErrorResponse} from "../types/advancedAnalysis";

export interface AnalysisOptions {
  userId: string;
  logId: string;
  locale?: string;
  jwtToken: string;
  force?: boolean; // Bypass cache and force fresh analysis
}

export interface AnalysisCache {
  result: AnalysisResult;
  timestamp: number;
}

/**
 * Cache for analysis results (in-memory, per session)
 * Key format: `${userId}:${logId}`
 */
const analysisCache = new Map<string, AnalysisCache>();

/**
 * Cache duration in milliseconds (24 hours)
 */
const CACHE_DURATION_MS = 24 * 60 * 60 * 1000;

/**
 * Check if advanced analysis exists for a log
 *
 * @param userId - User ID
 * @param logId - Log ID
 * @param jwtToken - JWT authentication token
 * @returns true if analysis exists (200), false if not exists (404 or error)
 */
export const checkAnalysisExists = async (userId: string, logId: string, jwtToken: string): Promise<boolean> => {
  try {
    const path = `/advancedAnalysis/${userId}/${logId}`;
    await head({
      apiName: "app",
      path: path,
      options: {
        headers: {
          Authorization: jwtToken
        }
      }
    }).response;

    // If HEAD request succeeds (200), analysis exists
    return true;
  } catch (error: any) {
    const statusCode = error.response?.statusCode || 0;

    // 404 or 410: Analysis doesn't exist (expected)
    if (statusCode === 404 || statusCode === 410) {
      console.log(`[AdvancedAnalysis] Analysis not found for log ${logId} (${statusCode})`);
      return false;
    }

    // 403: Forbidden (auth issue)
    if (statusCode === 403) {
      console.warn(`[AdvancedAnalysis] Access forbidden for log ${logId}`);
      return false;
    }

    // 500+: Server error (log but return false)
    if (statusCode >= 500) {
      console.error(`[AdvancedAnalysis] Server error checking analysis existence for log ${logId}: ${statusCode}`);
      return false;
    }

    // Other errors
    console.log(`[AdvancedAnalysis] Error checking analysis existence for log ${logId}:`, error);
    return false;
  }
};

/**
 * Load an existing advanced analysis from S3
 * Does NOT consume tokens - only retrieves saved results
 *
 * @param userId - User ID
 * @param logId - Log ID
 * @param jwtToken - JWT authentication token
 * @returns Analysis result or throws error
 * @throws AnalysisErrorResponse on API errors
 */
export const loadExistingAnalysis = async (
  userId: string,
  logId: string,
  jwtToken: string
): Promise<AnalysisResult> => {
  // Check in-memory cache first
  const cached = getCachedAnalysis(userId, logId);
  if (cached) {
    const cacheAge = getCacheAge(userId, logId);
    console.log(`[AdvancedAnalysis] Using cached result (age: ${cacheAge} minutes)`);
    return cached;
  }

  try {
    // Load from backend retrieval endpoint (no token consumption)
    console.log(`[AdvancedAnalysis] Loading existing analysis for log ${logId}`);
    const path = `/advancedAnalysis/${userId}/${logId}`;
    const {body} = await get({
      apiName: "app",
      path: path,
      options: {
        headers: {
          Authorization: jwtToken
        }
      }
    }).response;

    const result = (await body.json()) as any;

    console.log("[AdvancedAnalysis] Existing analysis loaded successfully");
    console.log("[AdvancedAnalysis] IssuesFound count:", result.IssuesFound?.length || 0);
    console.log("[AdvancedAnalysis] Recommendations count:", result.Recommendations?.length || 0);

    // Cache the result
    const cacheKey = `${userId}:${logId}`;
    analysisCache.set(cacheKey, {
      result: result as AnalysisResult,
      timestamp: Date.now()
    });

    return result as AnalysisResult;
  } catch (error: any) {
    const statusCode = error.response?.statusCode || 0;

    // Parse API Gateway error response
    if (error.response) {
      let errorData: any = {};
      try {
        errorData = await error.response.body.json();
      } catch (parseError) {
        // Body might be empty or not JSON
        console.warn("[AdvancedAnalysis] Failed to parse error response body");
      }

      // Map status codes to specific error codes
      let code = errorData.error?.code || "UNKNOWN_ERROR";
      let message = errorData.error?.message || "Failed to load analysis";

      if (statusCode === 404) {
        code = "NOT_FOUND";
        message = "Analysis not found";
      } else if (statusCode === 410) {
        code = "NOT_FOUND";
        message = "Analysis no longer available";
      } else if (statusCode === 403) {
        code = "FORBIDDEN";
        message = "Access denied to this analysis";
      } else if (statusCode >= 500) {
        code = "SERVER_ERROR";
        message = "Server error loading analysis";
      }

      throw {
        error: {
          code,
          message,
          statusCode,
          details: errorData.error?.details
        }
      } as AnalysisErrorResponse;
    }

    // Network or other errors
    throw {
      error: {
        code: "NETWORK_ERROR",
        message: error.message || "Failed to connect to analysis service",
        statusCode: 0
      }
    } as AnalysisErrorResponse;
  }
};

/**
 * Perform NEW advanced analysis on a log file
 * CONSUMES 1 TOKEN - creates fresh analysis
 *
 * @param userId - User ID
 * @param logId - Log ID
 * @param jwtToken - JWT authentication token
 * @param locale - Language locale for recommendations (default: "en-US")
 * @returns Analysis result or throws error
 * @throws AnalysisErrorResponse on API errors
 */
export const createNewAnalysis = async (
  userId: string,
  logId: string,
  jwtToken: string,
  locale: string = "en-US"
): Promise<AnalysisResult> => {
  // Clear any cached result - we're creating fresh
  console.log(`[AdvancedAnalysis] Creating new analysis for log ${logId} (will consume 1 token)`);
  clearAnalysisCache(userId, logId);

  try {
    // Call creation endpoint (consumes token)
    const path = `/analysis/${userId}/${logId}`;
    const {body} = await get({
      apiName: "app",
      path: path,
      options: {
        headers: {
          Authorization: jwtToken
        },
        queryParams: {
          locale
        }
      }
    }).response;

    const result = (await body.json()) as any;

    console.log("[AdvancedAnalysis] New analysis created successfully");
    console.log("[AdvancedAnalysis] IssuesFound count:", result.IssuesFound?.length || 0);
    console.log("[AdvancedAnalysis] Recommendations count:", result.Recommendations?.length || 0);
    console.log("[AdvancedAnalysis] SystemAssessment:", result.SystemAssessment);

    // Cache the new result
    const cacheKey = `${userId}:${logId}`;
    analysisCache.set(cacheKey, {
      result: result as AnalysisResult,
      timestamp: Date.now()
    });

    return result as AnalysisResult;
  } catch (error: any) {
    const statusCode = error.response?.statusCode || 0;

    // Parse API Gateway error response
    if (error.response) {
      let errorData: any = {};
      try {
        errorData = await error.response.body.json();
      } catch (parseError) {
        // Body might be empty or not JSON
        console.warn("[AdvancedAnalysis] Failed to parse error response body");
      }

      // Map status codes to specific error codes
      let code = errorData.error?.code || "UNKNOWN_ERROR";
      let message = errorData.error?.message || "Analysis failed";

      if (statusCode === 404) {
        code = "NOT_FOUND";
        message = "Log file not found";
      } else if (statusCode === 410) {
        code = "GONE";
        message = "Log file no longer available";
      } else if (statusCode === 403) {
        code = "FORBIDDEN";
        message = "Access denied to create analysis";
      } else if (statusCode >= 500) {
        code = "SERVER_ERROR";
        message = "Server error creating analysis";
      }

      throw {
        error: {
          code,
          message,
          statusCode,
          details: errorData.error?.details
        }
      } as AnalysisErrorResponse;
    }

    // Network or other errors
    throw {
      error: {
        code: "NETWORK_ERROR",
        message: error.message || "Failed to connect to analysis service",
        statusCode: 0
      }
    } as AnalysisErrorResponse;
  }
};

/**
 * Perform advanced analysis on a log file
 *
 * @deprecated Use loadExistingAnalysis() or createNewAnalysis() instead.
 *             This function always creates a new analysis (consuming tokens).
 *             For better control, use loadExistingAnalysis() to retrieve saved results
 *             or createNewAnalysis() to explicitly create a new analysis.
 *
 * @param options - Analysis options including userId, logId, locale, and jwtToken
 * @returns Analysis result or throws error
 * @throws AnalysisErrorResponse on API errors
 */
export const performAnalysis = async (options: AnalysisOptions): Promise<AnalysisResult> => {
  const {userId, logId, locale = "en-US", jwtToken, force = false} = options;

  // Check cache first (unless force is true)
  const cacheKey = `${userId}:${logId}`;
  const cached = analysisCache.get(cacheKey);

  if (!force && cached) {
    const age = Date.now() - cached.timestamp;
    if (age < CACHE_DURATION_MS) {
      console.log(`[AdvancedAnalysis] Using cached result (age: ${Math.round(age / 1000 / 60)} minutes)`);
      return cached.result;
    } else {
      // Cache expired
      analysisCache.delete(cacheKey);
    }
  }

  if (force) {
    console.log("[AdvancedAnalysis] Force flag set - bypassing cache");
    analysisCache.delete(cacheKey);
  }

  // Delegate to createNewAnalysis (backward compatibility)
  return createNewAnalysis(userId, logId, jwtToken, locale);
};

/**
 * Clear cached analysis for a specific log
 *
 * @param userId - User ID
 * @param logId - Log ID
 */
export const clearAnalysisCache = (userId: string, logId: string): void => {
  const cacheKey = `${userId}:${logId}`;
  analysisCache.delete(cacheKey);
};

/**
 * Clear all cached analyses
 */
export const clearAllAnalysisCache = (): void => {
  analysisCache.clear();
};

/**
 * Get cached analysis if available and not expired
 *
 * @param userId - User ID
 * @param logId - Log ID
 * @returns Cached result or null
 */
export const getCachedAnalysis = (userId: string, logId: string): AnalysisResult | null => {
  const cacheKey = `${userId}:${logId}`;
  const cached = analysisCache.get(cacheKey);

  if (cached) {
    const age = Date.now() - cached.timestamp;
    if (age < CACHE_DURATION_MS) {
      return cached.result;
    } else {
      analysisCache.delete(cacheKey);
    }
  }

  return null;
};

/**
 * Get cache age in minutes for a specific analysis
 *
 * @param userId - User ID
 * @param logId - Log ID
 * @returns Age in minutes or null if not cached
 */
export const getCacheAge = (userId: string, logId: string): number | null => {
  const cacheKey = `${userId}:${logId}`;
  const cached = analysisCache.get(cacheKey);

  if (cached) {
    return Math.round((Date.now() - cached.timestamp) / 1000 / 60);
  }

  return null;
};

/**
 * Load demo analysis from local testdata
 * Does NOT require authentication - loads from local demo file
 *
 * @param logId - Demo log ID (not used, always loads demo-analysis.json)
 * @returns Analysis result or throws error
 */
export const loadDemoAnalysis = async (logId: string): Promise<AnalysisResult> => {
  try {
    console.log(`[AdvancedAnalysis] Loading demo analysis for log ${logId}`);

    // Load from local testdata directory
    const demoUrl = "/testdata/demo-analysis-v3.json";

    const response = await fetch(demoUrl);

    if (!response.ok) {
      throw new Error(`Failed to load demo analysis: ${response.statusText}`);
    }

    const result = (await response.json()) as AnalysisResult;

    console.log("[AdvancedAnalysis] Demo analysis loaded successfully");
    console.log("[AdvancedAnalysis] IssuesFound count:", result.IssuesFound?.length || 0);
    console.log("[AdvancedAnalysis] Recommendations count:", result.Recommendations?.length || 0);

    return result;
  } catch (error: any) {
    console.error("[AdvancedAnalysis] Failed to load demo analysis:", error);
    throw {
      error: {
        code: "DEMO_LOAD_ERROR",
        message: error.message || "Failed to load demo analysis",
        statusCode: 0
      }
    } as AnalysisErrorResponse;
  }
};
